// src/utils/imageUploadUtils.js
// Loop Reservas — safe image upload helper.
// Normalizes Apple HEIC/HEIF photos into JPG before crop/upload flows.

export const IMAGE_UPLOAD_ACCEPT = "image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.heic,.heif";

const DEFAULT_MAX_MB = 18;
const HEIC2ANY_CDN = "https://cdn.jsdelivr.net/npm/heic2any@0.0.4/dist/heic2any.min.js";
const SUPPORTED_TYPES = new Set(["image/jpeg", "image/jpg", "image/png", "image/webp"]);
const SUPPORTED_EXTENSIONS = new Set(["jpg", "jpeg", "png", "webp"]);
const HEIC_EXTENSIONS = new Set(["heic", "heif"]);

export function imageFileExtension(file) {
  const name = String(file?.name || "").toLowerCase();
  const parts = name.split(".");
  return parts.length > 1 ? parts.pop() : "";
}

export function imageFileBaseName(file, fallback = "loop-image") {
  const name = String(file?.name || fallback).trim() || fallback;
  return name.replace(/\.[^.]+$/, "") || fallback;
}

export function isHeicLikeImage(file) {
  const ext = imageFileExtension(file);
  const type = String(file?.type || "").toLowerCase();
  return HEIC_EXTENSIONS.has(ext) || type.includes("heic") || type.includes("heif");
}

function loadImage(file, timeoutMs = 8000) {
  return new Promise((resolve, reject) => {
    if (!file) {
      reject(new Error("No image file"));
      return;
    }

    const url = URL.createObjectURL(file);
    const img = new Image();
    let done = false;
    const finish = (ok, value) => {
      if (done) return;
      done = true;
      try { URL.revokeObjectURL(url); } catch (_) {}
      if (ok) resolve(value);
      else reject(value instanceof Error ? value : new Error(String(value || "Image load failed")));
    };

    img.onload = () => finish(true, img);
    img.onerror = () => finish(false, new Error("Browser could not decode image"));
    img.src = url;
    setTimeout(() => finish(false, new Error("Image load timeout")), timeoutMs);
  });
}

function canvasToBlob(canvas, type = "image/jpeg", quality = 0.92) {
  return new Promise((resolve, reject) => {
    try {
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
        else reject(new Error("Canvas export failed"));
      }, type, quality);
    } catch (err) {
      reject(err);
    }
  });
}

async function convertReadableImageToJpegFile(file, namePrefix = "loop-image") {
  const img = await loadImage(file, 8000);
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, img.naturalWidth || img.width || 1);
  canvas.height = Math.max(1, img.naturalHeight || img.height || 1);
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Canvas unavailable");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
  const blob = await canvasToBlob(canvas, "image/jpeg", 0.92);
  return new File([blob], `${imageFileBaseName(file, namePrefix)}.jpg`, {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
}

function loadHeic2Any() {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("HEIC conversion requires a browser"));
      return;
    }
    if (typeof window.heic2any === "function") {
      resolve(window.heic2any);
      return;
    }

    const existing = document.querySelector('script[data-loop-heic2any="true"]');
    if (existing) {
      existing.addEventListener("load", () => {
        if (typeof window.heic2any === "function") resolve(window.heic2any);
        else reject(new Error("heic2any did not load"));
      }, { once: true });
      existing.addEventListener("error", () => reject(new Error("Could not load heic2any")), { once: true });
      return;
    }

    const script = document.createElement("script");
    script.src = HEIC2ANY_CDN;
    script.async = true;
    script.defer = true;
    script.setAttribute("data-loop-heic2any", "true");
    script.onload = () => {
      if (typeof window.heic2any === "function") resolve(window.heic2any);
      else reject(new Error("heic2any unavailable after load"));
    };
    script.onerror = () => reject(new Error("Could not load heic2any"));
    document.head.appendChild(script);
  });
}

async function convertHeicToJpegFile(file, namePrefix = "loop-image") {
  // Safari can sometimes decode HEIC natively. Try canvas first because it is faster.
  try {
    return await convertReadableImageToJpegFile(file, namePrefix);
  } catch (_) {
    // Chrome/Edge/Firefox usually need heic2any.
  }

  const heic2any = await loadHeic2Any();
  const converted = await heic2any({ blob: file, toType: "image/jpeg", quality: 0.92 });
  const blob = Array.isArray(converted) ? converted[0] : converted;
  if (!blob) throw new Error("HEIC conversion returned empty blob");
  return new File([blob], `${imageFileBaseName(file, namePrefix)}.jpg`, {
    type: "image/jpeg",
    lastModified: Date.now(),
  });
}

export async function prepareImageFileForUpload(file, options = {}) {
  const {
    maxMb = DEFAULT_MAX_MB,
    setStatus = null,
    purpose = "imagen",
    namePrefix = "loop-image",
    validateDecode = true,
  } = options || {};

  const status = (value) => {
    try { if (typeof setStatus === "function") setStatus(value || ""); } catch (_) {}
  };

  if (!file) return { ok: false, message: "No encontramos la imagen seleccionada." };

  const sizeMb = Number(file.size || 0) / (1024 * 1024);
  if (sizeMb > maxMb) {
    return { ok: false, message: `La ${purpose} pesa ${sizeMb.toFixed(1)}MB. Usa una imagen menor a ${maxMb}MB.` };
  }

  try {
    let workingFile = file;
    let converted = false;

    if (isHeicLikeImage(file)) {
      status("Convirtiendo HEIC a JPG...");
      workingFile = await convertHeicToJpegFile(file, namePrefix);
      converted = true;
    }

    const ext = imageFileExtension(workingFile);
    const type = String(workingFile.type || "").toLowerCase();
    if (type && !SUPPORTED_TYPES.has(type) && !SUPPORTED_EXTENSIONS.has(ext)) {
      return { ok: false, message: `Formato no soportado. Usa JPG, PNG, WEBP o HEIC/HEIF para ${purpose}.` };
    }
    if (!type && !SUPPORTED_EXTENSIONS.has(ext)) {
      return { ok: false, message: `No pudimos reconocer el formato. Usa JPG, PNG, WEBP o HEIC/HEIF para ${purpose}.` };
    }

    if (validateDecode) {
      status(converted ? "HEIC convertido. Validando JPG..." : "Validando imagen...");
      await loadImage(workingFile, 8000);
    }

    status("");
    return { ok: true, file: workingFile, converted };
  } catch (err) {
    console.warn("[imageUploadUtils] prepare failed", err);
    status("");
    return {
      ok: false,
      message: `No pudimos procesar esa ${purpose}. Si viene de iPhone/Mac, intenta nuevamente o exporta como JPG.`,
    };
  }
}

export async function prepareImageFilesForUpload(files, options = {}) {
  const list = Array.from(files || []).filter(Boolean);
  const prepared = [];
  for (let i = 0; i < list.length; i += 1) {
    const file = list[i];
    const result = await prepareImageFileForUpload(file, {
      ...options,
      setStatus: options?.setStatus ? (msg) => options.setStatus(msg ? `${msg} (${i + 1}/${list.length})` : "") : null,
    });
    if (!result.ok) return { ok: false, message: result.message, files: prepared };
    prepared.push(result.file);
  }
  return { ok: true, files: prepared };
}
